/**
 * SnoozeDialog — Snooze-Dialog mit Pflicht-Kommentar + optionalem Wiedervorlage-Datum
 * (Plan 02-05 Task 2, LIST-05). Deckt einzeln (anzahl=1) und Bulk (anzahl=n) mit
 * demselben Dialog ab. Anders als das Vorbild-Programm zusaetzlich ein optionales
 * Datumsfeld `wiedervorlageAm`.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@libero/core/components/ui/dialog';
import { Button } from '@libero/core/components/ui/button';

interface SnoozeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anzahl: number;
  onConfirm: (kommentar: string, wiedervorlageAm: string | null) => Promise<void>;
}

export default function SnoozeDialog({ open, onOpenChange, anzahl, onConfirm }: SnoozeDialogProps) {
  const [kommentar, setKommentar] = useState('');
  const [wiedervorlageAm, setWiedervorlageAm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKommentar('');
      setWiedervorlageAm('');
      setError(null);
    }
  }, [open]);

  async function handleSave() {
    const trimmed = kommentar.trim();
    if (!trimmed) {
      setError('Kommentar ist Pflicht.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onConfirm(trimmed, wiedervorlageAm || null);
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Snooze konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#1A1A1A]">
            {anzahl === 1 ? 'Artikel snoozen' : `${anzahl} Artikel snoozen`}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-600">
          {anzahl === 1
            ? 'Dieser Artikel wird temporaer aus der Liste ausgeblendet, bis er manuell wieder aufgehoben wird (oder das Wiedervorlage-Datum erreicht ist).'
            : `Alle ${anzahl} ausgewaehlten Artikel werden temporaer ausgeblendet, bis sie manuell wieder aufgehoben werden (oder das Wiedervorlage-Datum erreicht ist).`}
        </p>

        <div className="space-y-1">
          <label htmlFor="snooze-kommentar" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
            Kommentar (Pflicht)
          </label>
          <textarea
            id="snooze-kommentar"
            value={kommentar}
            onChange={(e) => setKommentar(e.target.value)}
            rows={3}
            className="w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
            placeholder="Warum wird dieser Artikel gesnoozed?"
            autoFocus
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="snooze-wiedervorlage" className="text-xs font-bold text-gray-600 uppercase tracking-wide">
            Wiedervorlage am (optional)
          </label>
          <input
            id="snooze-wiedervorlage"
            type="date"
            value={wiedervorlageAm}
            onChange={(e) => setWiedervorlageAm(e.target.value)}
            className="w-full rounded border border-[#DAD9D9] px-2 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
          />
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !kommentar.trim()}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            {saving ? 'Speichert…' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
